import { useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '../../ui/Modal'
import { deliveriesAPI } from '../../../api/deliveries'
import { FAILURE_REASONS } from '../../../constants/failureReasons'

interface Props {
  deliveryId: string
  trackingCode: string
  currentAttempts: number
  maxAttempts: number
  onClose: () => void
  onSuccess: () => void
}

export const EchecModal = ({
  deliveryId, trackingCode, currentAttempts, maxAttempts, onClose, onSuccess,
}: Props) => {
  const [reason,    setReason]    = useState('')
  const [note,      setNote]      = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error,     setError]     = useState('')

  const isLastAttempt = currentAttempts + 1 >= maxAttempts

  const handleSubmit = async () => {
    if (!reason) { setError('Sélectionnez un motif'); return }
    setIsLoading(true)
    setError('')
    try {
      await deliveriesAPI.signalerEchec(deliveryId, { reason, note: note.trim() || undefined })
      const next = currentAttempts + 1
      if (next >= maxAttempts) {
        toast.error(`Tentative ${next}/${maxAttempts} — L'admin va décider du sort du colis`)
      } else {
        toast.success(`Échec signalé — tentative ${next}/${maxAttempts}`)
      }
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du signalement')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open title="Signaler un échec" onClose={onClose} size="sm">
      {/* Sous-titre */}
      <p style={{ margin: '-4px 0 16px', fontSize: 13, color: '#6B7280' }}>
        {trackingCode} · Tentative {currentAttempts + 1} / {maxAttempts}
      </p>

      {/* Barre de tentatives */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {Array.from({ length: maxAttempts }).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 6, borderRadius: 4,
            background: i < currentAttempts ? '#EF4444'
                      : i === currentAttempts ? '#F59E0B'
                      : '#E5E7EB',
          }} />
        ))}
      </div>

      {/* Avertissement dernière tentative */}
      {isLastAttempt && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: 10, padding: '12px 14px', marginBottom: 16,
          fontSize: 13, color: '#991B1B',
        }}>
          <strong>Dernière tentative.</strong> Après cet échec, l'admin devra décider
          entre reprogrammation exceptionnelle ou retour du colis.
        </div>
      )}

      {/* Motifs */}
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
        Motif d'échec *
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {FAILURE_REASONS.map(r => (
          <label key={r.value} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
            border: `1px solid ${reason === r.value ? '#EF4444' : '#E5E7EB'}`,
            background: reason === r.value ? '#FEF2F2' : '#fff',
            fontSize: 13, transition: 'all .15s',
          }}>
            <input
              type="radio" name="reason" value={r.value}
              checked={reason === r.value}
              onChange={() => { setReason(r.value); setError('') }}
              style={{ accentColor: '#EF4444' }}
            />
            {r.label}
          </label>
        ))}
      </div>

      {/* Note libre */}
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        Note (optionnel)
      </label>
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Précisions : interphone cassé, code d'entrée manquant…"
        rows={3}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 10,
          border: '1px solid #E5E7EB', fontSize: 13, resize: 'vertical',
          fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />

      {error && (
        <p style={{ color: '#EF4444', fontSize: 13, marginTop: 8 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button
          onClick={onClose}
          disabled={isLoading}
          style={{
            flex: 1, padding: '11px 0', borderRadius: 12,
            border: '1px solid #E5E7EB', background: '#F9FAFB',
            color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !reason}
          style={{
            flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
            background: '#EF4444', color: '#fff', fontSize: 14, fontWeight: 600,
            cursor: isLoading || !reason ? 'not-allowed' : 'pointer',
            opacity: isLoading || !reason ? 0.6 : 1, fontFamily: 'inherit',
          }}
        >
          {isLoading ? 'Envoi...' : 'Confirmer l\'échec'}
        </button>
      </div>
    </Modal>
  )
}
